import { formatDate, formatExpirationMonthDay } from '@/features/shared/utils/date'
import {
  MembershipInterval,
  SubscriptionId,
} from '@/features/account/services/MembershipPlan/types'
import { CardBrand, type PaymentMethod } from '@/features/purchase/services/CreditCards/types'
import {
  PlanInterval,
  SubscriptionPlanType,
  type AuxMembershipPlan,
} from '@/features/account/services/SubscriptionAux/types'
import { formatPrice } from '@/features/shared/utils/formatter'
import { getMembershipType } from '@/features/account/components/Membership/getMembershipType'

// plan refers to membership options for non-subscribed users
// plans are returned from the subscription auxiliary service
export const getPlanInformation = (plan: AuxMembershipPlan | null) => {
  const {
    interval: unlimited_interval,
    total_cost_cents,
    total_base_cents,
    trial_period_days,
    plan_type,
    display,
  } = plan ?? {}

  const price = (total_cost_cents ?? 0) / 100
  const originalPrice = Number(total_base_cents) / 100
  const renewalPrice = Number(total_base_cents) / 100
  const trialDays = trial_period_days ?? 0

  const canUpgrade =
    unlimited_interval !== PlanInterval.ANNUAL && plan_type !== SubscriptionPlanType.STUDENT

  let name: string, billingFrequency: string, interval: string
  switch (unlimited_interval) {
    case PlanInterval.MONTHLY:
      name = 'Monthly'
      billingFrequency = 'monthly'
      interval = MembershipInterval.month
      break
    case PlanInterval.ANNUAL:
    default:
      name = 'Annual'
      billingFrequency = 'annually'
      interval = MembershipInterval.year
      break
  }
  if (plan_type === SubscriptionPlanType.STUDENT) {
    name = 'Student'
  } else if (plan_type === SubscriptionPlanType.EBT) {
    name = 'SNAP EBT'
  }

  const isDiscounted = price < originalPrice
  const discountAmount = originalPrice - price
  const includesRefund = display?.includes_refund

  const billingDate = new Date()
  billingDate.setDate(billingDate.getDate() + trialDays)

  // set billing date to renewal date of original price
  if (isDiscounted) {
    billingDate.setFullYear(billingDate.getFullYear() + 1)
  }
  const billingDateString = formatDate(billingDate, 'MM/dd/yyyy')
  const expirationDate = formatExpirationMonthDay(display?.promo_expires_at)

  return {
    ...plan,
    name,
    nameWithArticle: display?.name_with_article,
    billingFrequency,
    price,
    originalPrice,
    isDiscounted,
    discountAmount,
    interval,
    canUpgrade,
    billingDate: billingDateString,
    renewalPrice,
    trialDays,
    autoRenewalTerms: plan?.display?.auto_renewal ?? {},
    includesRefund,
    expirationDate,
  }
}

export const getAnnualMonthlyCost = (annualCost: number) => {
  return (annualCost / 12).toFixed(2)
}

/**
 * Used to calculate a users annual saving if they
 * were to upgrade to a more cost effective plan
 *
 * @param currentPlanCost Cost of current plan
 * @param currentPlanInterval Payment interval of current plan in months
 * @param upgradePlanCost Cost of upgrade plan
 * @param upgradePlanInterval Payment interval of upgrade plan in months
 * @returns {number} Total cost savings per year compared to current plan
 * @type {(currentPlanCost: number,currentPlanInterval: number,upgradePlanCost: number,upgradePlanInterval: number) => number}
 */
export const getAnnualCostSavings = (
  currentPlanCost: number,
  currentPlanInterval: number,
  upgradePlanCost: number,
  upgradePlanInterval: number,
) => {
  const rawSavings =
    12 * (currentPlanCost / currentPlanInterval - upgradePlanCost / upgradePlanInterval)
  return Math.max(0, Math.floor(rawSavings))
}

const isMembershipPromo = (plan: AuxMembershipPlan): boolean =>
  plan.total_cost_cents < plan.total_base_cents

type PromoPricingDetails = {
  intervalText: 'month' | 'year'
  promoIterations: number
  promoPriceText: string
  promoDurationText: string
  renewalPriceCents: number
}

export const formatRenewalPrice = (cents: number, trimWholeDollars = false): string => {
  const formatted = formatPrice(cents / 100)
  return trimWholeDollars && formatted.endsWith('.00') ? formatted.slice(0, -3) : formatted
}

export const getPromoPricingDetails = (plan: AuxMembershipPlan): PromoPricingDetails | null => {
  if (!isMembershipPromo(plan)) return null

  const display = plan.display
  const promoIterations = display?.promo_iterations ?? 0
  const promoPriceText = display?.price_text

  if (promoIterations <= 0 || !promoPriceText) return null

  const intervalText = plan.interval === 'monthly' ? 'month' : 'year'
  const promoDurationText =
    display.price_period_text ??
    (promoIterations === 1 ? `first ${intervalText}` : `first ${promoIterations} ${intervalText}s`)

  return {
    intervalText,
    promoIterations,
    promoPriceText,
    promoDurationText,
    renewalPriceCents: plan.total_base_cents,
  }
}

export const getIterativePromoRenewalText = (plan: AuxMembershipPlan): string => {
  const promoDetails = getPromoPricingDetails(plan)
  if (!promoDetails || promoDetails.promoIterations <= 1) return ''

  const { promoIterations: iterationCount, intervalText, renewalPriceCents } = promoDetails
  const formattedBasePrice = formatRenewalPrice(renewalPriceCents, true)

  return `Then ${formattedBasePrice}/${intervalText} after ${iterationCount} ${intervalText}s`
}

/**
 * Only certain payment methods can be used for membership purchases
 * since a membership is a reoccurring purchase. EBT, digital wallets,
 * and native pay are not allowed to be used for reoccurring purchases.
 *
 * Native pay is not listed in this function because it is only
 * initialized on the checkout page and filtered from the API response.
 */
export function isMembershipPaymentMethod(pm: PaymentMethod) {
  return (
    !pm.is_ebt &&
    pm.brand !== CardBrand.CashApp &&
    pm.brand !== CardBrand.Klarna &&
    pm.brand !== CardBrand.Link
  )
}

type SubscriptionPlan = {
  id?: '' | SubscriptionId | null
  interval?: '' | MembershipInterval | null
  interval_count?: number | null
  intervalCount?: number | null
  trial_period_days?: number | null
}
export const formatPlanType = (plan: SubscriptionPlan) => {
  const { id, interval } = plan
  const intervalCount = 'intervalCount' in plan ? plan.intervalCount : plan.interval_count
  const membershipType = getMembershipType(
    interval ?? MembershipInterval.year,
    intervalCount ?? 1,
  ).toLocaleLowerCase()

  // default to annual plan
  let planType: string = id ?? SubscriptionId.GROCERY_YEARLY
  let planTerm = membershipType === 'membership' ? 'annual' : membershipType

  const isStudent = planType === SubscriptionId.STUDENT_MONTHLY
  const isSnapEBT = planType === SubscriptionId.SNAP_EBT_MONTHLY

  if (isStudent) {
    planTerm = 'Student'
    planType = 'Student'
  } else if (isSnapEBT) {
    planType = 'ebt'
    planTerm = 'ebt'
  } else if (planType.startsWith(SubscriptionId.GROCERY_MONTHLY)) {
    // if plan is a monthly type, we only return grocery_monthly without any pricing info
    planType = SubscriptionId.GROCERY_MONTHLY.toLowerCase()
  } else if (planType.startsWith(SubscriptionId.GROCERY_YEARLY)) {
    // if plan is an annual type, return grocery_yearly without pricing/trial info
    planType = SubscriptionId.GROCERY_YEARLY.toLowerCase()
  }

  return {
    planTerm: planTerm.toLocaleLowerCase().replace(/ /g, '_'),
    planType: planType.toLocaleLowerCase(),
  }
}

export const getPlanStartedType = (plan: SubscriptionPlan) => {
  const { planType } = formatPlanType(plan)
  const isAnnualGrocery = planType === SubscriptionId.GROCERY_YEARLY.toLocaleLowerCase()
  const isTrial = (plan.trial_period_days ?? 0) > 0

  if (isAnnualGrocery && isTrial) {
    return `${planType}_with_trial`
  }

  return planType
}
